import { useCallback, useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { runPython } from '../components/PyodideRunner';
import KonspektPythonCodeMirror from './KonspektPythonCodeMirror';

export const KONSPEKT_DEFAULT_PYTHON = 'print("Привет")';

async function copyToClipboard(text, onDone) {
  try {
    await navigator.clipboard.writeText(text);
    onDone?.(true);
    return true;
  } catch {
    try {
      window.prompt('Скопируйте код вручную (Ctrl+C):', text);
    } catch {
      /* ignore */
    }
    onDone?.(false);
    return false;
  }
}

function KonspektPyRunNodeView({ node, updateAttributes, editor }) {
  const isAuthor = editor.isEditable;
  const [readerCode, setReaderCode] = useState(() => node.attrs.code || '');
  const [readerStdin, setReaderStdin] = useState(() => node.attrs.stdin || '');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);

  const code = isAuthor ? node.attrs.code || '' : readerCode;
  const stdin = isAuthor ? node.attrs.stdin || '' : readerStdin;

  const onCodeChange = useCallback(
    (next) => {
      if (isAuthor) updateAttributes({ code: next });
      else setReaderCode(next);
    },
    [isAuthor, updateAttributes],
  );

  const onStdinChange = useCallback(
    (e) => {
      const v = e.target.value;
      if (isAuthor) updateAttributes({ stdin: v });
      else setReaderStdin(v);
    },
    [isAuthor, updateAttributes],
  );

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleRun = async () => {
    if (running) return;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setOutput('');
    let acc = '';
    try {
      const result = await runPython(code, stdin, {}, {
        signal: ac.signal,
        onStdoutChunk: (chunk) => {
          acc += chunk;
          setOutput(acc);
        },
      });
      if (ac.signal.aborted) return;
      if (result.success) {
        const text = (result.output ?? acc).trimEnd();
        setOutput(text.length ? text : '(нет вывода)');
      } else {
        const errText = (result.error || 'Ошибка').trim();
        const out = (result.output || acc || '').trimEnd();
        setOutput(out ? `${out}\n\n${errText}` : errText);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setOutput(String(err.message || err));
    } finally {
      if (!ac.signal.aborted) setRunning(false);
    }
  };

  const handleCopy = () => {
    copyToClipboard(code, (ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  return (
    <NodeViewWrapper className="konspekt-py-run-wrap" data-drag-handle="">
      <div className="konspekt-py-run-head">
        <span className="konspekt-py-run-title">Пример с запуском (Python)</span>
        <div className="konspekt-py-run-actions">
          <button type="button" className="konspekt-py-btn" onClick={handleCopy} disabled={running}>
            {copied ? 'Скопировано' : 'Копировать код'}
          </button>
          <button type="button" className="konspekt-py-btn konspekt-py-btn-primary" onClick={handleRun} disabled={running}>
            {running ? 'Выполняется…' : 'Запустить'}
          </button>
        </div>
      </div>
      <KonspektPythonCodeMirror value={code} onChange={onCodeChange} readOnly={false} height="min(36vh, 200px)" />
      <label className="konspekt-py-stdin-label">
        Ввод (stdin)
        <textarea
          className="konspekt-py-stdin"
          value={stdin}
          onChange={onStdinChange}
          rows={3}
          placeholder="Текст для input() — по одной строке на вызов"
          spellCheck={false}
        />
      </label>
      <div className="konspekt-py-out-label">Вывод</div>
      <pre className="konspekt-py-output" aria-live="polite">
        {output || '—'}
      </pre>
    </NodeViewWrapper>
  );
}

function KonspektCodeStaticNodeView({ node, updateAttributes, editor }) {
  const isAuthor = editor.isEditable;
  const [copied, setCopied] = useState(false);
  const code = node.attrs.code || '';

  const handleCopy = () => {
    copyToClipboard(code, (ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  return (
    <NodeViewWrapper className="konspekt-code-static-wrap" data-drag-handle="">
      <div className="konspekt-code-static-head">
        <span className="konspekt-code-static-title">Пример кода</span>
        <button type="button" className="konspekt-py-btn" onClick={handleCopy}>
          {copied ? 'Скопировано' : 'Копировать код'}
        </button>
      </div>
      <KonspektPythonCodeMirror
        value={code}
        onChange={isAuthor ? (c) => updateAttributes({ code: c }) : undefined}
        readOnly={!isAuthor}
        height="min(32vh, 180px)"
      />
    </NodeViewWrapper>
  );
}

export const KonspektPyRun = Node.create({
  name: 'konspektPyRun',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      code: { default: KONSPEKT_DEFAULT_PYTHON },
      stdin: { default: '' },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="konspekt-py-run"]',
        getAttrs: (el) => ({
          code: safeDecode(el.getAttribute('data-code')),
          stdin: safeDecode(el.getAttribute('data-stdin')),
        }),
      },
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'konspekt-py-run',
        'data-code': encodeURIComponent(node.attrs.code || ''),
        'data-stdin': encodeURIComponent(node.attrs.stdin || ''),
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(KonspektPyRunNodeView);
  },
});

export const KonspektCodeStatic = Node.create({
  name: 'konspektCodeStatic',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      code: { default: KONSPEKT_DEFAULT_PYTHON },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="konspekt-code-static"]',
        getAttrs: (el) => ({
          code: safeDecode(el.getAttribute('data-code')),
        }),
      },
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'konspekt-code-static',
        'data-code': encodeURIComponent(node.attrs.code || ''),
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(KonspektCodeStaticNodeView);
  },
});

function safeDecode(raw) {
  if (raw == null || raw === '') return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
